import { collection, doc, getDoc, getDocs, limit, query, writeBatch, serverTimestamp } from 'firebase/firestore';
import { db } from './config';
import { buildCompositeLithologyName } from './dictionaries';
import { resolveLayerColorWithSource } from '../utils/layerColors';

// Zaokraglenie do 2 miejsc po przecinku (precyzja cm) — neutralizuje float-point
// kumulacje typu 0.6 + 0.9 + 0.7 = 2.2000000000000002.
const roundDepth = (v) => Math.round((Number(v) || 0) * 100) / 100;

/**
 * Pobierz mapę kolorów warstw geologicznych z catNrGeologyLayer.
 * Ścieżka: currentProjects/{projectId}/catNrGeologyLayer
 * @param {string} projectId
 * @returns {Promise<Object>} - mapa { "IIIa 1": "#c69658", ... }
 */
export const fetchProjectLayerColors = async (projectId) => {
  const colRef = collection(db, 'currentProjects', projectId, 'catNrGeologyLayer');
  const snapshot = await getDocs(colRef);
  const colorMap = {};
  snapshot.docs.forEach(d => {
    const data = d.data();
    const layerName = d.id; // np. "IIIa 1"
    // decodeColor format: "0xffc69658" (ARGB) -> "#c69658" (RGB)
    if (data.decodeColor) {
      const hex = data.decodeColor.replace(/^0x../, '#');
      colorMap[layerName] = hex;
    } else if (data.codeColor) {
      // fallback: codeColor to int ARGB -> hex RGB
      const intVal = parseInt(data.codeColor, 10);
      if (!isNaN(intVal)) {
        const r = (intVal >> 16) & 0xff;
        const g = (intVal >> 8) & 0xff;
        const b = intVal & 0xff;
        colorMap[layerName] = `#${r.toString(16).padStart(2,'0')}${g.toString(16).padStart(2,'0')}${b.toString(16).padStart(2,'0')}`;
      }
    }
  });
  return colorMap;
};

/**
 * Pobierz liste projektow z kolekcji currentProjects.
 * Zwraca: [{ id, title }, ...]
 */
export const fetchProjects = async () => {
  const colRef = collection(db, 'currentProjects');
  const snapshot = await getDocs(colRef);
  return snapshot.docs.map(d => ({
    id: d.id,
    title: d.data().title || '(brak nazwy)',
  }));
};

/**
 * Pobierz liste otworow z podkolekcji infoBorehole danego projektu.
 * @param {string} projectId - ID dokumentu projektu
 * Zwraca: [{ id, nameBorehole, coordZ, ...reszta pol }, ...]
 */
export const fetchProjectBoreholes = async (projectId) => {
  const colRef = collection(db, 'currentProjects', projectId, 'infoBorehole');
  const snapshot = await getDocs(colRef);
  return snapshot.docs.map(d => ({
    id: d.id,
    ...d.data(),
  }));
};

/**
 * Sprawdza ktore otwory maja jakikolwiek wpis w podkolekcji UserData
 * (czyli sa "wykonane" — ktos je opisal w aplikacji mobilnej).
 *
 * Nie da sie tego wyczytac z samego infoBorehole: zaden tamtejszy flag
 * (state, probe, manualStatus, coordZ) nie koreluje z obecnoscia UserData.
 * Collection group tez odpada — dokumenty UserData nie maja pola z projektem.
 * Zostaje jedno zapytanie limit(1) per otwor, dlatego wolamy to leniwie
 * (dopiero po wlaczeniu filtra) i cache'ujemy per projekt.
 *
 * @param {string} projectId
 * @param {string[]} boreholeIds
 * @returns {Promise<Set<string>>} - id otworow ktore maja UserData
 */
export const fetchExecutedBoreholeIds = async (projectId, boreholeIds) => {
  const executed = new Set();
  // Porcjami, zeby nie zalac przegladarki setkami rownoleglych zapytan naraz.
  const CHUNK = 25;
  for (let i = 0; i < boreholeIds.length; i += CHUNK) {
    const chunk = boreholeIds.slice(i, i + CHUNK);
    const results = await Promise.all(chunk.map(async (bhId) => {
      try {
        const ref = collection(db, 'currentProjects', projectId, 'infoBorehole', bhId, 'UserData');
        const snap = await getDocs(query(ref, limit(1)));
        return snap.empty ? null : bhId;
      } catch (err) {
        console.error(`Blad sprawdzania UserData dla ${bhId}:`, err);
        return null;
      }
    }));
    results.forEach(id => { if (id) executed.add(id); });
  }
  return executed;
};

/**
 * Pobierz liste wszystkich wersji UserData danego otworu.
 * Sluzy do UI wyboru wersji w "Wybrane otwory".
 * @param {string} projectId
 * @param {string} boreholeId
 * @returns {Promise<Array<{id, userName, lastModifyTs}>>} - posortowane od najnowszej
 */
export const fetchBoreholeUserDataVersions = async (projectId, boreholeId) => {
  const userDataRef = collection(db, 'currentProjects', projectId, 'infoBorehole', boreholeId, 'UserData');
  const snap = await getDocs(userDataRef);
  const versions = snap.docs.map(d => {
    const data = d.data();
    const ts = data.lastModifyTs?.seconds || data.lastModifyTs || 0;
    return {
      id: d.id,
      userName: data.userName || '',
      lastModifyTs: ts,
    };
  });
  versions.sort((a, b) => b.lastModifyTs - a.lastModifyTs);
  return versions;
};

/**
 * Pobierz pelne dane otworu z warstwami.
 * Sciezka: currentProjects/{projectId}/infoBorehole/{boreholeId}/UserData/{najnowszy lub wybrany}/baseDescriptionBorehole
 *
 * @param {string} projectId
 * @param {string} boreholeId
 * @param {Object} [layerColors={}] - mapa kolorów warstw { "IIIa 1": "#c69658" }
 * @param {string|null} [userDataIdOverride=null] - jesli podany, wczytuje konkretna wersje UserData zamiast najnowszej
 * @returns {Promise<{name, depth, layers[]}>} - dane otworu w formacie edytora
 */
export const fetchBoreholeWithLayers = async (projectId, boreholeId, layerColors = {}, userDataIdOverride = null) => {
  // 1. Pobierz dane otworu (nameBorehole, coordZ)
  const boreholeRef = doc(db, 'currentProjects', projectId, 'infoBorehole', boreholeId);
  const boreholeSnap = await getDoc(boreholeRef);
  if (!boreholeSnap.exists()) {
    throw new Error(`Otwor ${boreholeId} nie istnieje`);
  }
  const boreholeData = boreholeSnap.data();

  // 2. Pobierz UserData — znajdz wybrana wersje albo najnowsza po lastModifyTs
  const userDataRef = collection(boreholeRef, 'UserData');
  const userDataSnap = await getDocs(userDataRef);

  if (userDataSnap.empty) {
    // Brak UserData — zwroc otwor bez warstw
    const dateExecutionRaw = boreholeData.dateExecution || null;
    const dateExecution = dateExecutionRaw && typeof dateExecutionRaw.toDate === 'function'
      ? { seconds: dateExecutionRaw.seconds, nanoseconds: dateExecutionRaw.nanoseconds }
      : dateExecutionRaw;
    return {
      name: boreholeData.nameBorehole || '(brak nazwy)',
      depth: boreholeData.coordZ || 0,
      layers: [],
      waterLevels: [],
      supervisingDrilling: boreholeData.supervisingDrilling || '',
      dateExecution,
      sourceProjectId: projectId,
      sourceBoreholeId: boreholeId,
      sourceUserDataId: null,
    };
  }

  // Wybor UserData: jesli userDataIdOverride pasuje to bierzemy go, inaczej najnowszy po lastModifyTs
  let chosenDoc = null;
  if (userDataIdOverride) {
    chosenDoc = userDataSnap.docs.find(d => d.id === userDataIdOverride) || null;
  }
  if (!chosenDoc) {
    let newestTs = 0;
    userDataSnap.docs.forEach(d => {
      const data = d.data();
      const ts = data.lastModifyTs?.seconds || data.lastModifyTs || 0;
      if (ts >= newestTs) {
        newestTs = ts;
        chosenDoc = d;
      }
    });
  }

  // 3. Pobierz warstwy z baseDescriptionBorehole, posortowane po 'from'
  const layersRef = collection(chosenDoc.ref, 'baseDescriptionBorehole');
  const layersSnap = await getDocs(layersRef);

  // Pomocnik: jesli warstwa nie ma 'to' w bazie, uzupelnij z 'from' kolejnej warstwy.
  const rawLayers = layersSnap.docs
    .map(d => ({ docId: d.id, data: d.data() }))
    .sort((a, b) => (a.data.from || 0) - (b.data.from || 0));

  const layers = rawLayers.map(({ docId, data }, index) => {
    const geoLayer = data.numberGeologyLayer || '';
    // colorSource zapamietujemy na warstwie — tryb laczenia 'bySoilGroup' podmienia
    // kolor na kolor grupy tylko gdy zrodlem byla zgadywanka (paleta litologii /
    // domyslny szary), a nie numer warstwy geotechnicznej ani reczne ustawienie.
    const { color, source: colorSource } = resolveLayerColorWithSource({
      numberGeologyLayer: geoLayer,
      lithologyRockSoil01: data.lithologyRockSoil01 || '',
      dictionaryColors: layerColors,
    });
    const fromVal = roundDepth(data.from);
    const nextFrom = index + 1 < rawLayers.length ? roundDepth(rawLayers[index + 1].data.from) : null;
    const toVal = data.to != null
      ? roundDepth(data.to)
      : (nextFrom != null ? nextFrom : roundDepth(fromVal + (Number(data.thickness) || 0)));
    const lithoFields = {
      lithologyRockSoil01: data.lithologyRockSoil01 || '',
      lithologyRockSoilFullName01: data.lithologyRockSoilFullName01 || '',
      // Znak przewarstwienia ("+", "/", "//") aplikacja zrodlowa zapisuje w `layering01/02`.
      // `layeringRockSoil01/02` to starsza nazwa tego samego pola — w projekcie
      // pfeptyumpcdmfqf jest pusta we WSZYSTKICH warstwach, wiec bez tego fallbacku
      // separatory nie renderowaly sie wcale (skroty sklejaly sie sama spacja).
      layeringRockSoil01: data.layeringRockSoil01 || data.layering01 || '',
      lithologyRockSoil02: data.lithologyRockSoil02 || '',
      lithologyRockSoilFullName02: data.lithologyRockSoilFullName02 || '',
      layeringRockSoil02: data.layeringRockSoil02 || data.layering02 || '',
      lithologyRockSoil03: data.lithologyRockSoil03 || '',
      lithologyRockSoilFullName03: data.lithologyRockSoilFullName03 || '',
    };
    const composite = buildCompositeLithologyName(lithoFields);
    return {
      id: index + 1,
      name: composite || '(brak nazwy)',
      compositeLithology: composite,
      thickness: roundDepth(Number(data.thickness) || (toVal - fromVal) || 1),
      from: fromVal,
      to: toVal,
      color,
      colorSource,
      pattern: 'none',
      soilState: data.stateSoil || '',
      geotechnicalLayer: geoLayer,
      ...lithoFields,
      genesisAndStratigraphy: data.genesisAndStratigraphy || '',
      materialType: data.materialType || 'grunt',
      sourceLayerId: docId,
      // Pelny snapshot wszystkich pol Firestore (colors, crackDistance, hardness0X,
      // humidity, disintegrationRock0X, sampleDepth0X, ~40 pol geotechnicznych ktorych
      // edytor nie obsluguje). Uzywany w saveBoreholeToSource przy batch.set() dla
      // osieroconych / scalonych warstw, zeby nie wyzerowac geotechniki w bazie.
      // mergeLayers robi {...master} - referencja do _sourceData mastera idzie do scalonej.
      _sourceData: data,
    };
  });

  // 4. Pobierz dane wody z waterDescriptionBorehole
  const waterRef = collection(chosenDoc.ref, 'waterDescriptionBorehole');
  const waterSnap = await getDocs(waterRef);

  const waterLevels = [];
  waterSnap.docs.forEach((d, index) => {
    const data = d.data();
    const type = data.type || '';
    const znaw = data.znaw != null ? Number(data.znaw) : null;
    const zust = data.zust != null ? Number(data.zust) : null;

    // Pomiń "Suchy" i "brak"
    if (!type || type === 'Suchy' || type === 'brak') return;

    if (type === 'Sączenie') {
      if (znaw != null) {
        waterLevels.push({
          id: index + 1,
          type: 'seepage',
          depth: znaw,
          label: `${waterLevels.length + 1}`,
          sourceWaterId: d.id,
        });
      }
    } else {
      // Zwierciadło swobodne, napięte, nawiercone z nieznanym ustabilizowanym —
      // edytor spłaszcza do jednego 'stabilized-drilled'; sourceType zachowuje
      // oryginalny string z bazy, żeby round-trip nie zgubił rozróżnienia.
      waterLevels.push({
        id: index + 1,
        type: 'stabilized-drilled',
        sourceType: type,
        depthFrom: zust != null ? zust : znaw,
        depthTo: znaw != null ? znaw : 0,
        label: `${waterLevels.length + 1}`,
        sourceWaterId: d.id,
      });
    }
  });

  // Metadane — kto opisał i kiedy wykonano. Dokumentator typowo per-UserData
  // (wersja opisu), dateExecution typowo per-infoBorehole (fizyczny otwór).
  // Czytamy z obu lokalizacji żeby nie zgubić w razie różnic w schemacie.
  const userDataDoc = chosenDoc.data();
  const supervisingDrilling = userDataDoc.supervisingDrilling || boreholeData.supervisingDrilling || '';
  const dateExecutionRaw = userDataDoc.dateExecution || boreholeData.dateExecution || null;
  console.log(`[fetchBorehole] ${boreholeData.nameBorehole || boreholeId} meta:`, {
    supervisingDrilling,
    dateExecution: dateExecutionRaw,
    docKeys: { userData: Object.keys(userDataDoc), info: Object.keys(boreholeData) },
  });
  // Firestore Timestamp → serializowalna postać {seconds, nanoseconds}, żeby
  // przeszło przez JSON projektu i nie zgubiło typu po reloadzie.
  const dateExecution = dateExecutionRaw && typeof dateExecutionRaw.toDate === 'function'
    ? { seconds: dateExecutionRaw.seconds, nanoseconds: dateExecutionRaw.nanoseconds }
    : dateExecutionRaw;

  return {
    name: boreholeData.nameBorehole || '(brak nazwy)',
    depth: boreholeData.coordZ || 0,
    layers,
    waterLevels,
    supervisingDrilling,
    dateExecution,
    sourceProjectId: projectId,
    sourceBoreholeId: boreholeId,
    sourceUserDataId: chosenDoc.id,
  };
};

/**
 * Konwertuje hex "#rrggbb" na format Firebase "0xffrrggbb" (ARGB).
 */
const hexToDecodeColor = (hex) => {
  if (!hex || typeof hex !== 'string') return null;
  const clean = hex.replace('#', '').toLowerCase();
  if (clean.length !== 6) return null;
  return `0xff${clean}`;
};

/**
 * Zapisuje pojedynczy otwor z powrotem do bazy (currentProjects).
 * Strategia: nadpisanie najnowszej wersji UserData, delete+insert dla warstw i wody.
 *
 * Pomija otwory ktore nie maja sourceBoreholeId (dodane recznie w edytorze).
 *
 * @param {Object} editorBorehole - otwor z edytora (musi miec sourceProjectId, sourceBoreholeId, sourceUserDataId)
 * @returns {Promise<{ok: boolean, reason?: string}>}
 */
export const saveBoreholeToSource = async (editorBorehole) => {
  const { sourceProjectId, sourceBoreholeId, sourceUserDataId } = editorBorehole;
  console.log(`[saveBoreholeToSource] start: ${editorBorehole.name || editorBorehole.id}`, {
    sourceProjectId, sourceBoreholeId, sourceUserDataId,
    layerCount: editorBorehole.layers?.length, waterCount: editorBorehole.waterLevels?.length,
  });

  if (!sourceProjectId || !sourceBoreholeId) {
    console.warn('[saveBoreholeToSource] skip: brak sourceProjectId lub sourceBoreholeId');
    return { ok: false, reason: 'manual' };
  }
  if (!sourceUserDataId) {
    console.warn('[saveBoreholeToSource] skip: brak sourceUserDataId — otwor nie ma do czego pisac');
    return { ok: false, reason: 'no-userdata' };
  }

  const boreholeRef = doc(db, 'currentProjects', sourceProjectId, 'infoBorehole', sourceBoreholeId);
  const userDataRef = doc(boreholeRef, 'UserData', sourceUserDataId);
  const layersColRef = collection(userDataRef, 'baseDescriptionBorehole');
  const waterColRef = collection(userDataRef, 'waterDescriptionBorehole');

  // 1. Pobierz istniejace dokumenty warstw i wody (potrzebujemy ref + id zeby
  //    dopasowac edytorskie warstwy do dokumentow w bazie po sourceLayerId).
  const [oldLayersSnap, oldWaterSnap] = await Promise.all([
    getDocs(layersColRef),
    getDocs(waterColRef),
  ]);
  const oldLayerById = new Map(oldLayersSnap.docs.map(d => [d.id, d]));
  const oldWaterById = new Map(oldWaterSnap.docs.map(d => [d.id, d]));

  const batch = writeBatch(db);

  // 2. Update infoBorehole — tylko edytowalne pola (merge zachowuje reszte)
  batch.update(boreholeRef, {
    nameBorehole: editorBorehole.name || '',
    coordZ: editorBorehole.depth || 0,
  });

  // 3. Update UserData — tylko lastModifyTs
  batch.update(userDataRef, {
    lastModifyTs: serverTimestamp(),
  });

  // 4. Usun te warstwy z bazy, ktorych juz nie ma w edytorze
  const editorLayerSourceIds = new Set(
    (editorBorehole.layers || []).map(l => l.sourceLayerId).filter(Boolean)
  );
  oldLayersSnap.docs.forEach(d => {
    if (!editorLayerSourceIds.has(d.id)) batch.delete(d.ref);
  });

  // 5. Update istniejacych warstw (po sourceLayerId) + insert nowych. 'from' przeliczane
  //    kumulatywnie zeby kolejnosc/wstawki/usuniecia w edytorze byly poprawne w bazie.
  let cumulativeFrom = 0;
  (editorBorehole.layers || []).forEach((layer) => {
    const thicknessNum = roundDepth(layer.thickness);
    cumulativeFrom = roundDepth(cumulativeFrom);
    const toVal = roundDepth(cumulativeFrom + thicknessNum);
    // 'lithologyRockSoil01' bierzemy z dedykowanego pola jesli ustawione (z dropdownu),
    // fallback do layer.name dla zgodnosci z prostym edytorem ktory tylko ustawia 'name'.
    const litho01 = layer.lithologyRockSoil01 || layer.name || '';
    const editableFields = {
      lithologyRockSoil01: litho01,
      lithologyRockSoilFullName01: layer.lithologyRockSoilFullName01 || '',
      layeringRockSoil01: layer.layeringRockSoil01 || '',
      lithologyRockSoil02: layer.lithologyRockSoil02 || '',
      lithologyRockSoilFullName02: layer.lithologyRockSoilFullName02 || '',
      layeringRockSoil02: layer.layeringRockSoil02 || '',
      // Zapis do OBU nazw pola — `layering01/02` to ta, ktora czyta aplikacja zrodlowa.
      // Bez tego zmiana przewarstwienia w edytorze nie byla widoczna poza Przekrojem.
      layering01: layer.layeringRockSoil01 || '',
      layering02: layer.layeringRockSoil02 || '',
      lithologyRockSoil03: layer.lithologyRockSoil03 || '',
      lithologyRockSoilFullName03: layer.lithologyRockSoilFullName03 || '',
      genesisAndStratigraphy: layer.genesisAndStratigraphy || '',
      materialType: layer.materialType || 'grunt',
      thickness: thicknessNum,
      from: cumulativeFrom,
      to: toVal,
      numberGeologyLayer: layer.geotechnicalLayer || '',
      stateSoil: layer.soilState || '',
    };
    if (layer.sourceLayerId && oldLayerById.has(layer.sourceLayerId)) {
      // Istniejaca warstwa — update zachowuje pola spoza piatki w bazie (Firestore merge)
      batch.update(oldLayerById.get(layer.sourceLayerId).ref, editableFields);
    } else {
      // Nowa warstwa lub osierocony sourceLayerId. batch.set() zastapilby caly dokument
      // tylko edytowalnymi polami i wyzerowalby pola geotechniczne (~40 pol). Bierzemy
      // baseline z _sourceData (snapshot z fetchBoreholeWithLayers) jesli jest, potem
      // nadpisujemy edytowalnymi. Jesli warstwa byla calkiem nowa (np. addLayer w UI),
      // _sourceData nie ma — zapisujemy tylko editableFields. Dla scalonej warstwy
      // (mergeLayers kopiuje przez {...master}) _sourceData mastera trafia tutaj
      // i pola geotechniczne mastera sa zachowane.
      const baseFields = layer._sourceData || {};
      const newLayerRef = doc(layersColRef);
      batch.set(newLayerRef, { ...baseFields, ...editableFields });
    }
    cumulativeFrom += Number(layer.thickness) || 0;
  });

  // 6. Usun poziomy wody, ktorych juz nie ma w edytorze
  const editorWaterSourceIds = new Set(
    (editorBorehole.waterLevels || []).map(w => w.sourceWaterId).filter(Boolean)
  );
  oldWaterSnap.docs.forEach(d => {
    if (!editorWaterSourceIds.has(d.id)) batch.delete(d.ref);
  });

  // 7. Update istniejacych poziomow wody + insert nowych
  (editorBorehole.waterLevels || []).forEach((water) => {
    let editableFields;
    if (water.type === 'seepage') {
      editableFields = {
        type: 'Sączenie',
        znaw: Number(water.depth) || 0,
        zust: null,
      };
    } else if (water.type === 'stabilized-drilled') {
      editableFields = {
        type: water.sourceType || 'Zwierciadło swobodne',
        znaw: Number(water.depthTo) || 0,
        zust: Number(water.depthFrom) || 0,
      };
    } else {
      return;
    }
    if (water.sourceWaterId && oldWaterById.has(water.sourceWaterId)) {
      batch.update(oldWaterById.get(water.sourceWaterId).ref, editableFields);
    } else {
      const newWaterRef = doc(waterColRef);
      batch.set(newWaterRef, editableFields);
    }
  });

  // 8. Zaktualizuj kolory w catNrGeologyLayer dla unikalnych warstw geotechnicznych
  const colorUpdates = new Map();
  (editorBorehole.layers || []).forEach(layer => {
    const geo = layer.geotechnicalLayer;
    const decoded = hexToDecodeColor(layer.color);
    if (geo && decoded && !colorUpdates.has(geo)) {
      colorUpdates.set(geo, decoded);
    }
  });
  colorUpdates.forEach((decodeColor, geoLayer) => {
    const colorRef = doc(db, 'currentProjects', sourceProjectId, 'catNrGeologyLayer', geoLayer);
    batch.set(colorRef, { decodeColor }, { merge: true });
  });

  try {
    await batch.commit();
    console.log(`[saveBoreholeToSource] commit OK: ${editorBorehole.name} -> UserData/${sourceUserDataId}/baseDescriptionBorehole`);
    // Weryfikacja: pobierz świeżo zapisany UserData i policz dokumenty + sprawdź ts
    try {
      const verifySnap = await getDoc(userDataRef);
      const verifyLayersSnap = await getDocs(layersColRef);
      console.log(`[saveBoreholeToSource] verify ${editorBorehole.name}:`, {
        userDataExists: verifySnap.exists(),
        lastModifyTs: verifySnap.data()?.lastModifyTs,
        layerDocCount: verifyLayersSnap.size,
      });
    } catch (verifyErr) {
      console.warn('[saveBoreholeToSource] verify failed', verifyErr);
    }
    return { ok: true };
  } catch (err) {
    console.error(`[saveBoreholeToSource] BATCH FAIL: ${editorBorehole.name}`, err);
    throw err;
  }
};

/**
 * Zapisuje wszystkie otwory ktore maja sourceBoreholeId.
 * Zwraca podsumowanie: ile zapisano, ile pominieto, ile bledow.
 */
export const saveAllBoreholesToSource = async (editorBoreholes) => {
  const results = await Promise.allSettled(
    (editorBoreholes || []).map(bh => saveBoreholeToSource(bh))
  );

  let saved = 0;
  let skippedManual = 0;
  let errors = 0;
  const errorMessages = [];

  results.forEach((r, i) => {
    const bhName = editorBoreholes[i]?.name || `#${i + 1}`;
    if (r.status === 'rejected') {
      errors++;
      errorMessages.push(`${bhName}: ${r.reason?.message || r.reason}`);
    } else if (r.value.ok) {
      saved++;
    } else if (r.value.reason === 'manual') {
      skippedManual++;
    } else {
      errors++;
      errorMessages.push(`${bhName}: ${r.value.reason}`);
    }
  });

  return { saved, skippedManual, errors, errorMessages };
};
